import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core'
import { ApplicationType } from 'src/app/services/dtos'
import { ApplicationCreatorState } from 'src/app/account/services/application-creator-state.service'
import { SyncPairAccessCache } from 'src/app/account/services/sync-pair-access-cache.service'
import { LegacyTemplateFlow, TestPageMockState } from '../../showcase-mock-state'

/**
 * Both real app-creation steps a customer with out-of-date access templates sees.
 *
 * The showcase seeds a fake sync pair, so it gets its own ApplicationCreatorState
 * and SyncPairAccessCache rather than mutating the root singletons. These instances
 * never receive setAccountId(), so saveToLocalStorage() is a no-op and nothing can
 * bleed into a tester's real app-creation session.
 */
@Component({
  selector: 'showcase-legacy-templates',
  templateUrl: './showcase-legacy-templates.component.html',
  styleUrls: ['./showcase-legacy-templates.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  providers: [ApplicationCreatorState, SyncPairAccessCache],
})
export class ShowcaseLegacyTemplatesComponent implements OnInit {
  // Remount latch: the two AccessService instances are built once inside the
  // testSyncPairAccess switchMap, so the values baked into them never refresh on
  // their own when the flow changes.
  legacyTemplateMounted = true

  constructor(
    private readonly state: TestPageMockState,
    private readonly applicationCreatorState: ApplicationCreatorState,
    private readonly accessCache: SyncPairAccessCache
  ) {}

  ngOnInit(): void {
    this.seedLegacyTemplateCreatorState()
  }

  get legacyTemplateFlow(): LegacyTemplateFlow {
    return this.state.legacyTemplateFlow
  }

  get legacyTemplateIsAzure(): boolean {
    return this.state.legacyTemplateIsAzure
  }

  get legacyTemplateFlowDescription(): string {
    switch (this.legacyTemplateFlow) {
      case 'stacksets':
        return 'Both endpoints are StackSet-managed, so the connect page skips the 3-step setup and renders "Update StackSets". Each command group shows a grey "Primary/Recovery access is out of date" notice above its TEST ACCESS block.'
      case 'azure':
        return 'Azure has no template version, so the choice step switches to the "missing newer access updates" wording. The recovery-first gate is released, so the primary card is not dimmed.'
      default:
        return 'Two CloudFormation access cards, each with a grey "Out of date" badge next to TEST ACCESS. CONTINUE stays disabled until the templates are updated.'
    }
  }

  setLegacyTemplateFlow(flow: string): void {
    this.state.legacyTemplateFlow = flow as LegacyTemplateFlow
    this.seedLegacyTemplateCreatorState()
    // The AWS flows share a sync pair key and differ only on managedByStackSet, so a
    // surviving entry would render the previous flow's access state.
    this.accessCache.clear()
    this.legacyTemplateMounted = false
    setTimeout(() => (this.legacyTemplateMounted = true))
  }

  // Gives the connect page a valid sync pair so it renders instead of redirecting
  // back to the details step — Azure needs GUID subscription IDs and Azure region
  // names to pass validAccountsAndRegions.
  //
  // These account ids, subscription ids and region names are the one set of mock
  // values that cannot carry a MOCK-TEST marker: validAccountsAndRegions() checks
  // them against validAwsAccountId (12 digits), validAzureSubscriptionId (GUID) and
  // the real region list. Repeated digits keep them obviously synthetic instead.
  private seedLegacyTemplateCreatorState(): void {
    const state = this.applicationCreatorState
    // Without a recorded update choice the connect page redirects to the choice step,
    // which would navigate the whole test page away on mount.
    state.templateChoice = 'update'
    if (this.legacyTemplateIsAzure) {
      state.applicationType = ApplicationType.AZURE
      state.sourceAccountId = '11111111-1111-1111-1111-111111111111'
      state.sourceRegion = 'eastus'
      state.targetAccountId = '22222222-2222-2222-2222-222222222222'
      state.targetRegion = 'westus2'
      // StackSets is AWS-only; leaving a stale choice here would suppress the advisory.
      state.accessMethod = null
      return
    }
    state.applicationType = ApplicationType.AWS
    state.sourceAccountId = '111111111111'
    state.sourceRegion = 'us-east-1'
    state.targetAccountId = '222222222222'
    state.targetRegion = 'us-west-2'
    state.accessMethod = this.legacyTemplateFlow === 'stacksets' ? 'stacksets' : 'cloudformation'
  }
}
